import { useCallback, useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { HechoPor } from '../ui/HechoPor';
import { estadoSync, observarSync, type EstadoSync } from '../store/sync';
import './plano.css';

/**
 * La composición del paso 9, compartida por la puerta de acceso y la pantalla de cuenta: el
 * símbolo que se nivela, la firma y el gesto de vincular.
 */

export type Fase = 'quieto' | 'pre' | 'acumula' | 'desliza' | 'hecho';

/** Acumulación 900 ms → pausa 200 → deslizamiento 220 (paso 1, sin cambios). */
export function useGesto(): [Fase, () => void] {
  const [fase, setFase] = useState<Fase>('quieto');
  const temporizadores = useRef<number[]>([]);

  useEffect(() => {
    const pendientes = temporizadores.current;
    return () => pendientes.forEach(clearTimeout);
  }, []);

  const iniciar = useCallback(() => {
    // Cada paso solo avanza desde el anterior: con la pestaña en segundo plano el
    // `requestAnimationFrame` puede llegar después de los temporizadores, y sin
    // esta guarda devolvería el gesto a «acumula» cuando ya había terminado.
    const avanzar = (de: Fase, a: Fase) => setFase((f) => (f === de ? a : f));
    setFase('pre');
    requestAnimationFrame(() => requestAnimationFrame(() => avanzar('pre', 'acumula')));
    temporizadores.current.push(
      window.setTimeout(() => {
        avanzar('pre', 'desliza');
        avanzar('acumula', 'desliza');
      }, 1100),
      window.setTimeout(() => avanzar('desliza', 'hecho'), 1320),
    );
  }, []);

  return [fase, iniciar];
}

export function useEstadoSync(): EstadoSync {
  const [estado, setEstado] = useState<EstadoSync>(estadoSync);
  useEffect(() => observarSync(setEstado), []);
  return estado;
}

/** La primera sincronización posterior a `desde` terminó sin nada pendiente. */
export function sincronizoDesde(estado: EstadoSync, desde: number): boolean {
  return (
    estado.estado === 'inactivo' &&
    estado.pendientes === 0 &&
    estado.ultimaVez !== null &&
    Date.parse(estado.ultimaVez) >= desde
  );
}

/**
 * El símbolo como indicador del paso 1: entero mientras la masa sube, y el corte
 * y el salto juntos al final. Las transiciones viven en `plano.css`, que también
 * resuelve el movimiento reducido (ya deslizado, con un fundido de 120 ms).
 */
export function SimboloQueSeNivela({ fase }: { fase: Fase }): ReactElement {
  const clip = `${useId()}-nivel`;
  return (
    <svg
      className="sinc__simbolo"
      viewBox="0 0 48 48"
      data-fase={fase}
      role="img"
      aria-label="selflevel"
    >
      <defs>
        <clipPath id={clip}>
          <rect className="sinc__simbolo-nivel" width="48" height="48" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <path
          className="sinc__simbolo-a"
          fill="currentColor"
          d="M27.56,6.84 A20,20 0 0,0 17.21,45.48 Z"
        />
        <path
          className="sinc__simbolo-b"
          fill="currentColor"
          d="M30.79,2.53 A20,20 0 0,1 20.44,41.16 Z"
        />
      </g>
    </svg>
  );
}

/** El logotipo (tratamiento Fiel: SELF 400 + LEVEL 500), el eslogan y la firma. */
export function Firma({ className }: { className: string }): ReactElement {
  return (
    <div className={`sinc__firma ${className}`}>
      <p className="sinc__marca">
        <span className="sinc__logotipo">
          SELF<span>LEVEL</span>
        </span>
        <span className="t-sm t-dim">It finds its own level.</span>
      </p>
      <HechoPor className="hecho-por--pegada" />
    </div>
  );
}

/** El error en línea, encima del botón. */
export function ErrorEnLinea({ mensaje }: { mensaje: string }): ReactElement {
  return (
    <div className="sinc__error" role="alert">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v5.5M12 16.5v.01" />
      </svg>
      <span>{mensaje}</span>
    </div>
  );
}
